import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { applyConsentSignals } from "../utils/scriptLoader";
import { saveConsentDB } from "../services/consentThunks";

const ConsentContext = createContext(null);

const applyScripts = (consentData) => {
  const analytics = !!consentData?.categories?.analytics;
  const marketing = !!consentData?.categories?.marketing;
  // Single unified consent update — fixes all four toggle combinations without
  // race conditions caused by enableGA + enableMeta overwriting each other.
  applyConsentSignals(analytics, marketing);
};

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const stored = localStorage.getItem("cookie_consent");

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setConsent(parsed);
        // Defer script injection slightly so initial render & hero paint are not blocked by GTM/Meta scripts
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          window.requestIdleCallback(() => applyScripts(parsed));
        } else {
          setTimeout(() => applyScripts(parsed), 300);
        }
      } catch (error) {
        console.log("not JSON data in localStorage");
      }
    }

    setIsLoading(false);
  }, []);

  const saveConsent = async (data) => {

    const dataString = JSON.stringify(data);
    localStorage.setItem("cookie_consent", dataString);
    setConsent(data);
    applyScripts(data);

    try {
      const result = await dispatch(saveConsentDB(data)).unwrap();

      if (result.success && result.consent_id) {
        const consentId = JSON.stringify(result.consent_id);
        localStorage.setItem("consentId", consentId);
      }
    } catch (error) {
      console.error("Failed to save consent in DB", error);
    }
  };

  const getConsent = (category) => {
    return consent?.categories?.[category] ?? false;
  };

  return (
    <ConsentContext.Provider
      value={{ consent, isLoading, saveConsent, getConsent }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => useContext(ConsentContext);
